import type { FilterableListing } from '../../types';
import type { ClassListing } from 'v2/api/listings';
import { sortDays } from './days';

// Sharetribe's built-in category keys; the search schema is fixed to these three.
const CATEGORY_LEVEL_KEYS = ['categoryLevel1', 'categoryLevel2', 'categoryLevel3'] as const;

// Production ids are case-mixed (`sport`, `Netball`), but the value ends up on a flyer,
// so URLs are written lowercase and every comparison ignores case.
export const normaliseCategoryId = (id: unknown) =>
  String(id ?? '')
    .trim()
    .toLowerCase();

const getCategoryIds = (listing: ClassListing) => {
  const publicData = listing.attributes.publicData ?? {};

  return CATEGORY_LEVEL_KEYS.map((key) => normaliseCategoryId(publicData[key])).filter(Boolean);
};

// Keep in sync with ProfilePage.js and ListingsByLocation.js: there are no location
// ids, so this display name is what the filter matches and the URL carries.
export const getLocationName = (listing: ClassListing): string | null => {
  const { location, shortLocation } = listing.attributes.publicData ?? {};

  return location?.building?.trim() || shortLocation || null;
};

export interface ToFilterableListingOptions {
  /** `meta.activeIds` from `view=all`. Leave out on the profile, which has no status. */
  activeIds?: ReadonlySet<string> | readonly string[];
}

const hasId = (ids: ReadonlySet<string> | readonly string[], id: string) =>
  'has' in ids ? ids.has(id) : ids.includes(id);

export const toFilterableListing = (
  listing: ClassListing,
  { activeIds }: ToFilterableListingOptions = {}
): FilterableListing => {
  const id = listing.id.uuid;
  const daysAvailable = listing.attributes.publicData?.daysAvailable;
  const categoryIds = getCategoryIds(listing);

  return {
    id,
    days: Array.isArray(daysAvailable) ? sortDays(daysAvailable) : [],
    categoryIds,
    leafCategoryId: categoryIds[categoryIds.length - 1] ?? null,
    location: getLocationName(listing),
    ...(activeIds ? { isActive: hasId(activeIds, id) } : {}),
  };
};
